import { ReactElement, useMemo } from "react";
import { ListItem, ListItemStyle } from "../ListItem";
import {
	LessonKind,
	MagisterAppointment,
	StatusKind
} from "client/Api/Workspace/LiveInformation/Magister/magisterLiveInformation";
import {
	DateString,
	Icon,
	IconValue,
	Space,
	WeekViewCardStyle
} from "client/Components";
import { logNever } from "client_legacy/ndaw/errors";
import styles from "./AppointmentListItem.module.less";
import { isNotNilOrEmpty } from "client/Misc/utils";
import { TimeZone } from "client/DateAndTime/timezones";

export type AppointmentListItemProps = {
	appointment: MagisterAppointment;
	onNavigate: () => void;
	timezone: TimeZone;
};

export const AppointmentListItem = (props: AppointmentListItemProps): ReactElement => {
	const {
		appointment,
		onNavigate,
		timezone
	} = props;

	const stylingOptions = useMemo(
		(): StylingOptions => computeAppointmentStylingOptions(appointment),
		[appointment]
	);

	return (
		<ListItem
			title={appointment.description}
			isHoverable
			onClick={onNavigate}
			style={stylingOptions.style}
		>
			<div className={styles.timeSpan}>
				<ListItem.Title>
					<DateString
						value={appointment.startDateTime}
						format="T"
						timezone={timezone}
					/>
				</ListItem.Title>
				<ListItem.Info>
					<DateString
						value={appointment.finishDateTime}
						format="T"
						timezone={timezone}
					/>
				</ListItem.Info>
			</div>
			<ListItem.Content>
				<ListItem.Row>
					<ListItem.Title>
						{appointment.description}
						<Space />
						-
						<Space />
						{appointment.location}
					</ListItem.Title>
					<ListItem.RightAddition>
						<ListItem.Title>
							{appointment.lessonNumberFrom}
						</ListItem.Title>
					</ListItem.RightAddition>
				</ListItem.Row>
				{stylingOptions.icons.length > 0 && (
					<ListItem.Row>
						<ListItem.RightAddition>
							<ListItem.Info>
								{stylingOptions.icons
									.map((x, i) => (
										<div
											key={i}
											className={styles.icon}
										>
											<Icon value={x} />
										</div>
									))
								}
							</ListItem.Info>
						</ListItem.RightAddition>
					</ListItem.Row>
				)}
			</ListItem.Content>
		</ListItem>
	);
};
AppointmentListItem.displayName = "AppointmentListItem";

export type StylingOptions = {
	style: (ListItemStyle & WeekViewCardStyle) | undefined;
	icons: readonly IconValue[];
};

export function computeAppointmentStylingOptions(
	appointment: MagisterAppointment
): StylingOptions {
	let style: ListItemStyle | undefined;
	const icons: IconValue[] = [];

	switch (appointment.lessonKind) {
		case LessonKind.Homework:
			icons.push("book");
			break;
		case LessonKind.Test:
		case LessonKind.Examination:
		case LessonKind.WrittenExamination:
		case LessonKind.OralExamination:
			style = "warning";
			icons.push({
				kind: "font",
				name: "file-lines",
				color: "warning"
			});
			break;
		case LessonKind.Information:
		case LessonKind.Unknown:
			break;
		default:
			logNever(appointment.lessonKind);
	}

	switch (appointment.status) {
		case StatusKind.Changed:
			icons.push("triangle-exclamation");
			break;
		case StatusKind.Cancelled:
			style = "danger";
			icons.push({
				kind: "font",
				name: "xmark",
				color: "danger"
			});
			break;
		case StatusKind.AutomaticScheduled:
		case StatusKind.WeDontKnowWhatItMeansTwo:
		case StatusKind.WeDontKnowWhatItMeansFour:
		case StatusKind.InUse:
		case StatusKind.Closed:
			break;
		default:
			logNever(appointment.status);
	}

	if (isNotNilOrEmpty(appointment.message)) {
		icons.push("circle-info");
	}

	return {
		style,
		icons
	};
}
